import type { OptimizelyDecision } from '@optimizely/optimizely-sdk'
import { OptimizelyDecideOption } from '@optimizely/optimizely-sdk'

const PDP_SCOPE_ATTRIBUTES = ['onSale', 'isCustoms'] as const

export const useOptimizely = () => {
  const {
    configP13n: { enableOptimizely },
    configOptimizelyWebExperiments: { enabled }
  } = useFeatureFlags()
  const auth = useAuthStore()
  const cart = useCartStore()
  const userStore = useUserStore()
  const route = useRoute()
  const region = useRegion()
  const { width, height } = useWindowSize()
  const optaNonConsentCookie = useCookie('OptanonConsent')
  const userId = useCookie('optimizelyUserId')
  const locationData = {
    country: useCookie<string>('Edgescape-Country'),
    state: useCookie<string>('Edgescape-State'),
    city: useCookie<string>('Edgescape-City'),
    zip: useCookie<string>('Edgescape-Zip'),
    latLong: useCookie<string>('Edgescape-Lat-Long')
  }
  const visitNumber = useCookie('optimizelyVisitNumber')
  const { $optimizely } = useNuxtApp()
  const optimizelyLandingPage = useCookie('optimizelyLandingPage')

  const decisions = useState<Map<string, OptimizelyDecision | undefined>>('p13n-decisions', () => new Map())

  const pathProductId = getProductIdFromPath(route.path).toUpperCase()
  const isInPdpContext = !!pathProductId
  const productsData = useState<Map<string, any>>('optimizelyProductsData', () => new Map())

  const defineUser = () => {
    if (isInPdpContext && !productsData.value.has(pathProductId)) {
      const pdpData = usePdp()
      pdpData?.product.value && productsData.value.set(
        pathProductId,
        {
          onSale: pdpData.product.value.onSale,
          isCustoms: pdpData.product.value.dummyCustoms,
        }
      )
    }

    const attributes = getOptimizelyAttributes(
      {
        auth,
        cart,
        landingPage: Boolean(optimizelyLandingPage.value),
        locationData,
        productData: isInPdpContext ? productsData.value.get(pathProductId) : null,
        route,
        userStore,
        visitNumber: Number(visitNumber.value),
        windowSize: { width: width.value, height: height.value },
      }
    )
    if (window) {
      window.optimizely = window.optimizely || []
      window.optimizely.push({
        type: 'user',
        attributes
      })
    }
    console.info('Optimizely attributes:', attributes)
    return $optimizely.client?.createUserContext(userId.value as string, attributes)
  }

  const getTargetingConsent = (groups) => {
    const consent = checkConsentGroup(region === 'EMEA', route.query.preview === 'true', decodeURIComponent(optaNonConsentCookie.value || ''))
    return groups.every((key) => consent[key])
  }

  const decide = async (key: string) => {
    // should not attempt to decide if we are on server
    if (!enableOptimizely || !isClient()) return

    // if in PDP context, await product data to have all necessary attributes (onSale, isCustoms etc.), if needed
    if (isInPdpContext && PDP_SCOPE_ATTRIBUTES.some((attr) => $optimizely.isAttributeUsedByFlag(key, attr))) {
      const { product } = usePdp()
      await until(product).toMatch((product) => !!product && product.id.toUpperCase() === pathProductId)

      productsData.value.set(
        pathProductId,
        {
          onSale: product.value?.onSale,
          isCustoms: product.value?.dummyCustoms,
        }
      )
    }

    const user = getTargetingConsent(['targeting']) ? defineUser() : null
    decisions.value.set(key, user?.decide(key, [
      OptimizelyDecideOption.ENABLED_FLAGS_ONLY,
      OptimizelyDecideOption.IGNORE_USER_PROFILE_SERVICE,
    ]))
  }

  const trackEvent = (eventKey: string, properties, decimalRevenue?) => {
    if (!enableOptimizely) return
    const revenue = Math.round(decimalRevenue * 100)
    const user = getTargetingConsent(['targeting']) ? defineUser() : null
    const mappedProperties = mapProperties(eventKey, properties)
    const tags = {
      $opt_event_properties: mappedProperties,
      ...(revenue && { revenue })
    }
    if (window) {
      window.optimizely = window.optimizely || []
      window.optimizely.push({
        type: 'event',
        eventName: eventKey,
        tags: {
          ...(revenue && { revenue })
        },
        properties
      })
    }
    console.info(`Tracking ${eventKey} Event for Optimizely: tags were`, tags)
    user?.trackEvent(eventKey, tags)
  }

  const activateOptimizely = () => {
    if (!enabled || !isClient()) return
    window.userLoggedIn = auth?.loggedIn || false
    window.optimizely = window.optimizely || []
    window.optimizely.push({
      type: 'activate'
    })
    optimizelyLandingPage.value = ''
  }

  return {
    decide,
    decisions,
    defineUser,
    trackEvent,
    activateOptimizely
  }
}
